import { Message as MessageType, ThreadTab } from "./types";
import Filters from "byzantine/src/filters";

export function groupMessages(messages: MessageType[]) {
  // Unfortunately, we have two backend models that represent message content: Messages and ThreadFiles.
  // Our endpoint returns them in a flat list, but we want to group them together in the UI.
  // In the ideal case, a Message in the API response is followed by any ThreadFiles that are attached to it.
  // In practice, however, we have to deal with edge cases:
  // - Race conditions could cause Messages and their associated ThreadFiles not be returned in the correct sequence.
  //   We should treat these as standalone messages.
  // - ThreadFiles pre-2023 do not have authors, so we should treat them as standalone messages of
  //   uncertain authorship.

  const results: { message: MessageType | null; files: MessageType[] }[] = [];

  for (const message of messages) {
    if (!message.author) {
      // If the message doesn't have an author, don't group it with the previous message.
      if (message.type === "message") {
        results.push({ message, files: [] });
      } else {
        results.push({ message: null, files: [message] });
      }
    } else if (message.type === "message") {
      results.push({ message, files: [] });
    } else if (
      message.author.id !== results[results.length - 1]?.message?.author?.id &&
      message.author.id !== results[results.length - 1]?.files[0]?.author?.id
    ) {
      // If the message is a ThreadFile but the author doesn't match the previous message's author,
      // treat it as a standalone message.
      results.push({ message: null, files: [message] });
    } else {
      // We know that this is a ThreadFile whose author matches the previous Message's author,
      // so we can group it with the previous message.
      results[results.length - 1].files.push(message);
    }
  }

  return results;
}

// Replace this with findLastIndex once we support ES2023
export function getLastReadStaffMessageIndex(
  groupedMessages: {
    message: MessageType | null;
  }[]
) {
  for (let i = groupedMessages.length - 1; i >= 0; i--) {
    const message = groupedMessages[i].message;
    if (message && message.author?.is_staff && message.is_read) {
      return i;
    }
  }
  return -1;
}

export function getIdOfLastMessageExcludingInternalNotesAndFiles(
  groupedMessages: {
    message: MessageType | null;
  }[]
): string | null {
  for (let i = groupedMessages.length - 1; i >= 0; i--) {
    const message = groupedMessages[i].message;
    if (message && !message.internal_note && message.type === "message") {
      return message.id;
    }
  }
  return null;
}

export function statusToLabel(status: ThreadTab, userTeamCount?: number): string {
  switch (status) {
    case "assigned_to_my_team":
      if (!userTeamCount || userTeamCount <= 1) {
        return "Assigned to my team";
      }
      return "Assigned to my teams";
    case "waiting_for_response":
      return "Awaiting response";
    default:
      return Filters.humanize(status);
  }
}
